import { z } from 'zod';

import {
  idSchema,
  inlineImageSchema,
  isoDateTimeSchema,
  looseDateTimeSchema,
  ulidSchema,
} from '../common';
import {
  agentEventLevelSchema,
  agentStatusSchema,
  browserStatusSchema,
  commentRejectionReasonSchema,
  platformSchema,
  platformSessionStatusSchema,
  postSourceTypeSchema,
  searchCycleStatusSchema,
  searchQueryStatusSchema,
  selectorHealthOutcomeSchema,
} from '../enums';
import { selectorHealthRuleKeySchema } from '../rest/selector-packs';
import { defineEventSchema, eventUnion } from './envelope';

/**
 * Agent → Server messages.
 *
 * Everything a desktop agent is able to tell the cloud is in this file. The
 * shape of the channel is itself a privacy boundary: there is no message that
 * carries a cookie, a session token, a browser profile directory, a password or
 * a page's raw HTML. What leaves the user's machine is state (which is what the
 * dashboard renders), extracted post data (which is the product), and telemetry
 * about which selectors broke.
 *
 * The agent authenticates once, when it opens the socket. No message below
 * repeats that credential — a credential inside an application message would end
 * up in every log, replay buffer and error report that touches the frame.
 */

/**
 * Free-form structured context attached to logs and errors.
 *
 * `unknown` values, not `any`: the server stores and displays this blob and must
 * never assume anything about its shape. The agent scrubs it before sending (see
 * `agentLogSchema`), and the server treats it as untrusted display data anyway.
 */
export const agentContextSchema = z.record(z.string(), z.unknown());
export type AgentContext = z.infer<typeof agentContextSchema>;

// ---------------------------------------------------------------------------
// agent.hello
// ---------------------------------------------------------------------------

/**
 * First frame after the socket opens: what this build is and what it can
 * interpret.
 *
 * The device token is deliberately absent. Authentication happens during the
 * WSS handshake (connect time), so by the time this message exists the peer is
 * already identified. Repeating the secret here would put a long-lived
 * credential into the application message stream, where it would be logged.
 *
 * The two version numbers let the server decide, in one round trip, whether to
 * push a newer SelectorPack, or `agent.update_required` when the adapter is too
 * old to interpret the pack the workspace is on.
 */
export const agentHelloSchema = defineEventSchema('agent.hello', {
  agentId: idSchema,
  appVersion: z.string().trim().min(1).max(40),
  osVersion: z.string().trim().min(1).max(120),
  /** Interpreter generation of this build — one number for the whole agent. */
  adapterVersion: z.number().int().min(1),
  /**
   * SelectorPack version the agent has cached, PER PLATFORM. A missing key and
   * a 0 mean the same thing — the agent runs on its bundled baseline for that
   * platform and the server should push the published pack if one exists. Keyed
   * by platform because the packs are: a Facebook pack update says nothing
   * about whether the agent also holds the current Threads pack.
   */
  selectorPackVersions: z.record(platformSchema, z.number().int().min(0)).default({}),
  /**
   * Projects this agent believes it is already running, as of this connect.
   *
   * The same fact `agentHeartbeatSchema` carries, moved to the frame that arrives
   * FIRST. "Which project is running" lives only in the agent's process memory, so
   * a restart erases it while the server's row still says RUNNING; the server
   * reconciles the two and re-issues `project.start` for what the agent has
   * forgotten. Without this field that repair could not happen until the first
   * heartbeat, up to 30 seconds after the socket opened.
   *
   * OPTIONAL, and the absence is meaningful: an older agent build says nothing,
   * and "said nothing" must not be read as "runs nothing" — that reading would
   * re-issue `project.start` to an agent that is already running the project, on
   * every single reconnect. An absent field means "no answer, wait for a
   * heartbeat"; an empty array means "I am running none of them".
   */
  activeProjectIds: z.array(idSchema).max(200).optional(),
});
export type AgentHello = z.infer<typeof agentHelloSchema>;

// ---------------------------------------------------------------------------
// agent.heartbeat
// ---------------------------------------------------------------------------

/**
 * Liveness. `sentAt` is the agent's clock, so the server can measure skew rather
 * than trust it — an agent whose clock is far off would otherwise mis-evaluate
 * every command `expiresAt` locally.
 */
export const agentHeartbeatSchema = defineEventSchema('agent.heartbeat', {
  sentAt: isoDateTimeSchema,
  uptimeSeconds: z.number().int().min(0),
  /** Projects this agent believes it is running; the server reconciles against
   *  its own view and re-pushes configuration when the two disagree. */
  activeProjectIds: z.array(idSchema).max(200),
});
export type AgentHeartbeat = z.infer<typeof agentHeartbeatSchema>;

// ---------------------------------------------------------------------------
// agent.status
// ---------------------------------------------------------------------------

export const agentStatusMessageSchema = defineEventSchema('agent.status', {
  status: agentStatusSchema,
  detail: z.string().trim().max(1_000).optional(),
});
export type AgentStatusMessage = z.infer<typeof agentStatusMessageSchema>;

// ---------------------------------------------------------------------------
// browser.status
// ---------------------------------------------------------------------------

/**
 * One Chromium profile's lifecycle state.
 *
 * `USER_ACTION_REQUIRED` is reported — never worked around. It is the state the
 * agent enters on a checkpoint, a 2FA prompt, a CAPTCHA or an account
 * restriction, and the only resolution is the human in front of the machine.
 */
export const browserStatusMessageSchema = defineEventSchema('browser.status', {
  browserProfileId: idSchema,
  status: browserStatusSchema,
  detail: z.string().trim().max(1_000).optional(),
  /**
   * Whether a headed window is currently open for the user.
   *
   * Pure UI state — it says nothing about the session, and carries no cookie,
   * path or credential. It exists because `browser.status_changed` REQUIRES the
   * flag on the dashboard side and the server had nothing to fill it with, so
   * `browser_profiles.interactive_open` only ever changed on revoke and the
   * open/close button pair could not be trusted. Optional so an older agent
   * build simply leaves the stored value alone.
   */
  interactiveOpen: z.boolean().optional(),
});
export type BrowserStatusMessage = z.infer<typeof browserStatusMessageSchema>;

// ---------------------------------------------------------------------------
// platform.session_status
// ---------------------------------------------------------------------------

/**
 * DISPLAY IDENTITY ONLY.
 *
 * This is the single message that says anything about a platform account, and
 * it is limited to what is already rendered on screen: the visible name and the
 * avatar URL. Cookies, session tokens, `c_user`/`xs` values, the profile
 * directory and the password must NEVER appear here or in any other message —
 * they stay on the client machine for the whole life of the system. Adding a
 * field that carries one would break the product's central privacy promise.
 *
 * `platform` names which channel the reading is about. A browser profile is
 * bound to one platform at registration, so this is a cross-check rather than
 * an address — the server verifies it against the profile row and drops a
 * mismatch instead of writing a Threads reading over a Facebook one.
 *
 * `displayName` and `avatarUrl` are optional because a session can be observed
 * as logged-in before the header finishes rendering, and because CHECKPOINT and
 * LOGGED_OUT have no identity to report at all.
 */
export const platformSessionStatusMessageSchema = defineEventSchema('platform.session_status', {
  browserProfileId: idSchema,
  platform: platformSchema,
  sessionStatus: platformSessionStatusSchema,
  displayName: z.string().trim().max(200).optional(),
  avatarUrl: z.string().url().max(2_048).optional(),
  /**
   * Why the agent reports this status, in Vietnamese, for the operator.
   *
   * The agent already composed such sentences ("Không đọc được trạng thái đăng
   * nhập sau 10 phút — nhiều khả năng SelectorPack chưa khớp…") and sent them as
   * `detail`; the field did not exist here and `z.object` strips silently, so the
   * frame parsed, the status was stored, and the explanation was thrown away —
   * `browser_profiles.status_message` stayed NULL forever. Display text only, of
   * the same nature as `browserStatusMessageSchema.detail`.
   */
  detail: z.string().trim().max(1_000).optional(),
});
export type PlatformSessionStatusMessage = z.infer<typeof platformSessionStatusMessageSchema>;

// ---------------------------------------------------------------------------
// search.cycle_started
// ---------------------------------------------------------------------------

export const searchCycleStartedSchema = defineEventSchema('search.cycle_started', {
  searchCycleId: idSchema,
  projectId: idSchema,
  keywordCount: z.number().int().min(0).max(500),
  startedAt: isoDateTimeSchema,
});
export type SearchCycleStarted = z.infer<typeof searchCycleStartedSchema>;

// ---------------------------------------------------------------------------
// search.query_completed
// ---------------------------------------------------------------------------

/**
 * One keyword, finished.
 *
 * `search_queries` is the only per-keyword record this system has, and until
 * this frame existed nothing wrote to it: the table was empty across every cycle
 * ever run, and so were `search_cycles.keywords_completed`. That left the cycle
 * summary unable to answer the first question anyone asks of a thin result —
 * *which* keyword came back empty, and did it come back empty or not run at all.
 * A cycle that stopped after its first keyword and a cycle whose keywords all
 * genuinely found nothing were indistinguishable.
 *
 * Emitted per keyword as the cycle walks them, not batched at the end, so a
 * cycle that dies half-way still leaves a record of what it got through.
 *
 * `keywordId` is the id the agent was configured with. It is deliberately NOT a
 * foreign key on the way in — the whole point is that it may be stale, and the
 * server resolves it against the project's real keywords, falling back to
 * `term`. Both are sent because either alone can be the one that still matches.
 */
export const searchQueryCompletedSchema = defineEventSchema('search.query_completed', {
  searchCycleId: idSchema,
  projectId: idSchema,
  keywordId: idSchema,
  /** Snapshot of the term as searched, so the row stays readable after a rename. */
  term: z.string().trim().min(1).max(200),
  status: searchQueryStatusSchema,
  startedAt: isoDateTimeSchema,
  completedAt: isoDateTimeSchema,
  /** Cards the parser accepted for this keyword, before the server deduplicates. */
  resultsFound: z.number().int().min(0),
  /** Scroll rounds spent across every surface this keyword visited. A keyword
   *  that finds nothing after many scrolls reads very differently from one that
   *  stopped at the first screen. */
  scrollCount: z.number().int().min(0),
  error: z.string().trim().max(2_000).optional(),
});
export type SearchQueryCompleted = z.infer<typeof searchQueryCompletedSchema>;

// ---------------------------------------------------------------------------
// search.posts_batch
// ---------------------------------------------------------------------------

/**
 * A post exactly as the parser produced it.
 *
 * This schema is deliberately permissive. Facebook renders the same card a
 * dozen ways and randomises class names constantly, so a missing reaction count
 * or an unparsable timestamp is *ordinary*, not an error — rejecting a post over
 * it would throw away the one thing that matters, which is the post itself.
 * Every field is therefore optional except the collection timestamp, the
 * keywords that led the agent to the card, and the identity refinement below.
 *
 * The one non-negotiable is identity: without `externalPostId` or `permalink`
 * the server cannot deduplicate, so the row would create an unbounded stream of
 * near-duplicates in the global `posts` table. The agent drops such cards before
 * batching; a violation reaching here means an agent bug, and failing the whole
 * batch loudly is the right response to that.
 */
export const rawPostSchema = z
  .object({
    externalPostId: z.string().trim().min(1).max(128).optional(),
    /** Absolute URL. The parser applies `toAbsoluteUrl` and drops what it cannot
     *  normalise, so a relative href never reaches the server. */
    permalink: z.string().url().max(2_048).optional(),
    content: z.string().max(20_000).optional(),
    authorName: z.string().trim().max(200).optional(),
    authorUrl: z.string().url().max(2_048).optional(),
    authorExternalId: z.string().trim().max(128).optional(),
    authorAvatarUrl: z.string().url().max(2_048).optional(),
    /**
     * Accepts an ISO timestamp with or without an offset, because Facebook
     * exposes both `datetime` attributes and relative labels ("2 giờ trước")
     * that the parser converts locally. A label it cannot convert is omitted
     * rather than sent as free text — the server would have to guess a timezone,
     * and a wrong `postedAt` silently corrupts the freshness score.
     */
    postedAt: looseDateTimeSchema.optional(),
    /**
     * The RAW timestamp label when — and only when — the parser could not
     * convert it. "Đăng: —" told the operator nothing and told calibration
     * even less; the verbatim label ("16 giờ · Đã chỉnh sửa", scrambled
     * reconstructions…) is both a readable fallback for the UI and the exact
     * evidence needed to fix the parser. Display text, never a date: nothing
     * downstream may attempt to score freshness from it.
     */
    postedAtText: z.string().trim().min(1).max(120).optional(),
    /**
     * A picture of the timestamp, for the cases where reading it is beyond the
     * parser — sent **only** when `postedAt` is absent.
     *
     * WHY BYTES ARE ALLOWED TO LEAVE THE MACHINE AT ALL, on a channel whose
     * stated boundary is "state, extracted post data, and selector telemetry,
     * never raw page content". A crop of the timestamp label is extracted post
     * data that happens to still be pixels: it is the same field the parser was
     * trying to read, bounded to the one element the SelectorPack pointed at.
     * It is not a viewport screenshot and must never become one — a full-page
     * capture would carry the operator's own Facebook chrome, their name, their
     * notifications and whoever else's posts happened to be on screen. The agent
     * crops to the resolved element and nothing wider, and
     * `INLINE_IMAGE_MAX_BYTES` is the backstop for the day that goes wrong.
     *
     * Optional, and expected to be absent on nearly every post: a healthy
     * SelectorPack reads the time from a `datetime` attribute and never gets
     * here. A batch where most posts carry one is not a feature working, it is
     * the signal that Facebook changed the card and the pack needs calibrating.
     */
    postedAtImage: inlineImageSchema.optional(),
    reactionCount: z.number().int().min(0).optional(),
    commentCount: z.number().int().min(0).optional(),
    shareCount: z.number().int().min(0).optional(),
    /** MVP stores URLs and metadata only; media bytes are never downloaded. */
    mediaUrls: z.array(z.string().url().max(2_048)).max(20).optional(),
    sourceType: postSourceTypeSchema.optional(),
    groupName: z.string().trim().max(200).optional(),
    /**
     * Which of the project's keywords led here. Required: unlike the parsed
     * fields this is the agent's own bookkeeping, and the server needs it to
     * write `post_keyword_matches`.
     */
    matchedKeywordIds: z.array(idSchema).max(200),
    collectedAt: isoDateTimeSchema,
  })
  .refine((post) => post.externalPostId !== undefined || post.permalink !== undefined, {
    message: 'A post needs at least one of externalPostId or permalink to be deduplicated',
    path: ['externalPostId'],
  })
  /**
   * A post that already has a readable timestamp has no business carrying a
   * picture of one.
   *
   * Refused rather than ignored. The image exists to buy an answer from a paid
   * model, so a bug that attached one to every post would not break anything
   * visible — it would just spend money on questions already answered, at the
   * rate of one call per post, until somebody read the bill. Failing the frame
   * puts that in the agent's log on the first batch instead.
   */
  .refine((post) => post.postedAtImage === undefined || post.postedAt === undefined, {
    message: 'postedAtImage is only for posts whose postedAt could not be parsed',
    path: ['postedAtImage'],
  });
export type RawPost = z.infer<typeof rawPostSchema>;

/**
 * How many timestamp crops one batch may carry.
 *
 * Two limits in one number. It bounds the frame — 200 posts each holding 256 KB
 * would be a 50 MB message — and it bounds the spend, because every image is a
 * paid model call. Ten is above what a healthy pack ever produces (which is
 * zero) and far below a batch where the pack has stopped working entirely.
 *
 * WHEN THE PACK IS BROKEN, THIS IS THE RIGHT BEHAVIOUR AND NOT A REGRESSION. If
 * 200 posts in a row have unreadable timestamps, the answer is not to buy 200
 * model calls; it is to recalibrate the SelectorPack. The agent sends the first
 * ten as specimens and drops the rest, and `postedAtText` still carries the raw
 * label for every one of them — so the evidence needed for the fix survives
 * without the bill.
 */
export const MAX_POSTED_AT_IMAGES_PER_BATCH = 10;

/**
 * One chunk of a search cycle's harvest.
 *
 * `batchId` is a client-minted ULID and is the idempotency key: `post_batches`
 * carries `UNIQUE(agent_id, batch_id)`. After a network drop the agent MUST
 * resend the same `batchId` rather than mint a new one — that is what lets the
 * server recognise the resend instead of ingesting the posts twice.
 *
 * `sequence` orders chunks within a cycle and `isFinal` marks the last one, so
 * the server can tell "the cycle finished" from "the agent went away", without
 * waiting for `search.cycle_completed` that may never arrive.
 */
export const searchPostsBatchSchema = defineEventSchema('search.posts_batch', {
  batchId: ulidSchema,
  agentId: idSchema,
  projectId: idSchema,
  searchCycleId: idSchema,
  sequence: z.number().int().min(0),
  isFinal: z.boolean(),
  /** Capped so one frame cannot blow up parse time or memory on the API; the
   *  agent splits a large cycle across sequences instead. */
  posts: z
    .array(rawPostSchema)
    .max(200)
    .refine(
      (posts) =>
        posts.filter((post) => post.postedAtImage !== undefined).length <=
        MAX_POSTED_AT_IMAGES_PER_BATCH,
      {
        message: `A batch may carry at most ${MAX_POSTED_AT_IMAGES_PER_BATCH} timestamp crops; the agent drops the rest`,
      },
    ),
});
export type SearchPostsBatch = z.infer<typeof searchPostsBatchSchema>;

/**
 * The server's reply to `search.posts_batch` — sent on the same socket, in the
 * server → agent direction.
 *
 * MVP uses the synchronous ingestion path: the API validates, deduplicates and
 * upserts into `posts` / `post_matches` / `post_keyword_matches` **inside the
 * request**, and only then enqueues the scoring job in BullMQ. So this ack is a
 * statement about durable state, not about a queued intention — when the agent
 * sees it, the posts are committed.
 *
 * Because `post_batches` has `UNIQUE(agent_id, batch_id)`, a batch resent after
 * a network drop is recognised by its `batchId` and the server returns the
 * ORIGINAL ack instead of processing the posts a second time. The counts an
 * agent sees for a given `batchId` are therefore stable forever, and retrying is
 * always safe.
 */
export const searchPostsBatchAckSchema = defineEventSchema('search.posts_batch_ack', {
  batchId: ulidSchema,
  /** New rows written to `post_matches`. */
  accepted: z.number().int().min(0),
  /** Recognised by one of the five dedup rules and left unchanged. */
  duplicates: z.number().int().min(0),
  /** Existing posts whose counts or content were refreshed (a new score revision
   *  follows for these). */
  updated: z.number().int().min(0),
  rejected: z.number().int().min(0),
  /**
   * Reason → count histogram for the rejected posts. Aggregated rather than
   * per-post: a batch can carry hundreds of rows and the agent cannot act on an
   * individual rejection anyway. Per-post detail stays in the server log.
   */
  rejectedReasons: z.record(z.string(), z.number().int().min(0)).optional(),
});
export type SearchPostsBatchAck = z.infer<typeof searchPostsBatchAckSchema>;

// ---------------------------------------------------------------------------
// search.cycle_completed
// ---------------------------------------------------------------------------

/**
 * End of a cycle. `totalFound` counts what the parser saw, `totalSent` what
 * survived the agent's own filtering — the gap is the first place to look when
 * a selector starts to rot.
 */
export const searchCycleCompletedSchema = defineEventSchema('search.cycle_completed', {
  searchCycleId: idSchema,
  projectId: idSchema,
  status: searchCycleStatusSchema,
  totalFound: z.number().int().min(0),
  totalSent: z.number().int().min(0),
  finishedAt: isoDateTimeSchema,
  /** Present for FAILED, and for WAITING_NETWORK when a batch is still queued
   *  locally awaiting reconnection. */
  error: z.string().trim().max(2_000).optional(),
});
export type SearchCycleCompleted = z.infer<typeof searchCycleCompletedSchema>;

// ---------------------------------------------------------------------------
// comment.started / comment.result
// ---------------------------------------------------------------------------

export const commentStartedSchema = defineEventSchema('comment.started', {
  commentJobId: idSchema,
  postId: idSchema,
  startedAt: isoDateTimeSchema,
});
export type CommentStarted = z.infer<typeof commentStartedSchema>;

/**
 * Outcome of one `comment.execute` command.
 *
 * `rejectionReason` and `failureReason` are different things and both exist on
 * purpose. A rejection is a *decision*: the agent's local guard, the policy
 * engine or a rate limiter refused, and the job must not be retried blindly. A
 * failure is an *accident*: the box was not found, the page navigated away. When
 * the agent's guard rejects, its refusal wins over the server's opinion — the
 * server records the rejection instead of re-queueing around it.
 */
export const commentResultMessageSchema = defineEventSchema('comment.result', {
  commentJobId: idSchema,
  success: z.boolean(),
  externalCommentId: z.string().trim().max(128).optional(),
  failureReason: z.string().trim().max(2_000).optional(),
  rejectionReason: commentRejectionReasonSchema.optional(),
  /**
   * What the page said about a comment that DID go out — never an explanation
   * of a failure, which is `failureReason`'s job.
   *
   * It exists because "posted" is not one state on every platform. A Threads
   * reply to a post whose author gates replies is created, is invisible to
   * everyone else, and the page says so in words ("Đang chờ tác giả phê
   * duyệt"). Reporting that as a plain success hides a comment nobody can read;
   * reporting it as a failure invites a second send and a duplicate under the
   * operator's real name (both happened live, 20/08/2026). So the verdict stays
   * success and the page's own sentence travels with it.
   *
   * Copied from the page, never composed here — the agent quotes, it does not
   * interpret.
   */
  note: z.string().trim().max(300).optional(),
  finishedAt: isoDateTimeSchema,
});
export type CommentResultMessage = z.infer<typeof commentResultMessageSchema>;

// ---------------------------------------------------------------------------
// selector.health_report
// ---------------------------------------------------------------------------

/**
 * One rule's outcome over the reporting window.
 *
 * `candidateIndex` is the position in the rule's ordered candidate list that
 * actually resolved. A rule that keeps succeeding at index 3 is a rule whose
 * first two candidates are already dead — that is the early warning the health
 * table exists to surface, before the last candidate goes too and the whole
 * system stops collecting.
 */
export const selectorHealthEntrySchema = z.object({
  ruleKey: selectorHealthRuleKeySchema,
  outcome: selectorHealthOutcomeSchema,
  candidateIndex: z.number().int().min(0).max(32).optional(),
  occurrences: z.number().int().min(1),
  /** Null for SUCCESS and for FALLBACK_USED entries that never hard-failed; the
   *  entry still reports `occurrences`, which is what makes a failure rate
   *  computable across outcomes. */
  lastFailedAt: isoDateTimeSchema.nullable(),
  /** URL **path** only. A full Facebook URL can carry story ids and session
   *  parameters, which is exactly the kind of thing this channel must not leak. */
  sampleUrlPath: z.string().max(512).optional(),
  /**
   * Why the most recent occurrence failed, in the interpreter's own words.
   *
   * WHAT THIS BUYS. The health table can already say that `permalink` failed
   * 2 668 times; it could not say *how*, and the difference between "no
   * candidate matched" and "the card was dropped because its content exceeded
   * the schema" is the difference between recalibrating a selector and fixing a
   * parser. Without it, the largest single loss in the harvest — cards that are
   * fully parsed and then silently discarded — has no cause recorded anywhere,
   * and answering "why" needs a hand-attached log on a developer's machine.
   *
   * WHAT IT MUST NEVER CARRY. Page content. Every producer of this string builds
   * it from schema paths, rule names and candidate indices — never from a post
   * body, an author name or a URL. That is a property of the call sites, so it
   * is restated at each one; the 300-character cap here is a backstop against a
   * future producer that forgets, not the defence itself.
   *
   * One per (rule, outcome) group and last-write-wins, matching how the agent
   * aggregates: a sample, not a log.
   */
  sampleMessage: z.string().max(300).optional(),
});
export type SelectorHealthEntry = z.infer<typeof selectorHealthEntrySchema>;

/**
 * Selector telemetry. This is what turns "Facebook changed something" from an
 * outage into a dashboard row an admin can fix by publishing a new pack version —
 * no agent rebuild, no reinstall. It carries counts and rule names only, never
 * page content.
 */
export const selectorHealthReportSchema = defineEventSchema('selector.health_report', {
  /** Version the agent was actually running when it observed these outcomes,
   *  which may lag the published one by a cycle. */
  selectorPackVersion: z.number().int().min(0),
  entries: z.array(selectorHealthEntrySchema).max(200),
});
export type SelectorHealthReport = z.infer<typeof selectorHealthReportSchema>;

// ---------------------------------------------------------------------------
// agent.log / agent.error
// ---------------------------------------------------------------------------

/**
 * A log line forwarded for the dashboard's agent view.
 *
 * The agent scrubs before sending: cookies, `Authorization` values, device
 * tokens, Facebook session values, passwords and API keys are redacted by
 * `packages/logger` on the way out, so a secret never reaches the wire even
 * when an exception message happens to contain one. The server treats the text
 * as untrusted display data regardless and never parses it.
 */
export const agentLogSchema = defineEventSchema('agent.log', {
  level: agentEventLevelSchema,
  message: z.string().max(4_000),
  context: agentContextSchema.optional(),
});
export type AgentLog = z.infer<typeof agentLogSchema>;

/**
 * A structured error. `fatal` distinguishes "this cycle died" from "this agent
 * is going down": only the latter should move the agent to `ERROR` and pause the
 * projects bound to it.
 */
export const agentErrorSchema = defineEventSchema('agent.error', {
  /** Machine-readable, e.g. `BROWSER_LAUNCH_FAILED`, `PROFILE_LOCKED`. */
  code: z.string().trim().min(1).max(80),
  message: z.string().max(4_000),
  fatal: z.boolean(),
  context: agentContextSchema.optional(),
});
export type AgentError = z.infer<typeof agentErrorSchema>;

/**
 * The four outcomes an agent may assert about a command it was given.
 *
 * `PENDING`, `SENT` and `ACKNOWLEDGED` are deliberately absent: they describe
 * the server's own row lifecycle and are not the agent's to claim.
 */
export const COMMAND_ACK_STATUSES = ['COMPLETED', 'REJECTED', 'FAILED', 'EXPIRED'] as const;
export const commandAckStatusSchema = z.enum(COMMAND_ACK_STATUSES);
export type CommandAckStatus = z.infer<typeof commandAckStatusSchema>;

/**
 * What happened to one command.
 *
 * This frame exists because the outcome used to travel as a `context` bag on an
 * `agent.log` / `agent.error` line, and the server — reasonably — treated those
 * as telemetry and wrote them to `agent_events` without reading the bag. Nothing
 * ever called `recordAcknowledgement`, so every command in the system aged out:
 * 45 of 45 rows sat at `EXPIRED` with `acknowledged_at` and `completed_at` NULL,
 * even the ones that had plainly succeeded.
 *
 * The visible damage was worse than a wrong column. "Mở trong Chromium" returned
 * `status: 'SENT'` and the dashboard reported success — while the agent may have
 * refused the command outright, which it did, with `ERR_QUIC_PROTOCOL_ERROR`
 * recorded in `agent_events` where no operator would look.
 *
 * Making it its own frame rather than teaching the gateway to sniff log context
 * is the point: command state is not a side effect of logging. The log line is
 * still sent, because an operator reading the agent's journal should see it.
 */
export const agentCommandAckSchema = defineEventSchema('agent.command_ack', {
  commandId: z.string().trim().min(1).max(64),
  commandType: z.string().trim().min(1).max(80),
  status: commandAckStatusSchema,
  /** Why it was refused or how it failed. Untrusted display text. */
  message: z.string().max(2_000).optional(),
});
export type AgentCommandAck = z.infer<typeof agentCommandAckSchema>;

// ---------------------------------------------------------------------------
// The union
// ---------------------------------------------------------------------------

/**
 * Everything an agent may send. The gateway parses each frame with this union
 * and drops what does not match — so this union *is* the inbound allowlist, and
 * there is no separate list of accepted event names that could drift from it.
 *
 * `search.posts_batch_ack` is intentionally absent: it travels the other way.
 */
export const agentToServerMessageSchema = eventUnion([
  agentHelloSchema,
  agentHeartbeatSchema,
  agentStatusMessageSchema,
  browserStatusMessageSchema,
  platformSessionStatusMessageSchema,
  searchCycleStartedSchema,
  searchQueryCompletedSchema,
  searchPostsBatchSchema,
  searchCycleCompletedSchema,
  commentStartedSchema,
  commentResultMessageSchema,
  selectorHealthReportSchema,
  agentCommandAckSchema,
  agentLogSchema,
  agentErrorSchema,
]);
export type AgentToServerMessage = z.infer<typeof agentToServerMessageSchema>;
export type AgentToServerEvent = AgentToServerMessage['event'];
